import EarthIcon from '@src/svgs/EarthIcon';
import FacebookActionLikeIcon from '@src/svgs/FacebookActionLikeIcon';
import FacebookCommentActionIcon from '@src/svgs/FacebookCommentActionIcon';
import FacebookLikeReactionBadge from '@src/svgs/FacebookLikeReactionBadge';
import FacebookLoveReactionBadge from '@src/svgs/FacebookLoveReactionBadge';
import FacebookShareActionIcon from '@src/svgs/FacebookShareActionIcon';
import FacebookVerifiedBadge from '@src/svgs/FacebookVerifiedBadge';
import FacebookWowReactionBadge from '@src/svgs/FacebookWowReactionBadge';
import React from 'react';
import styled from 'styled-components';

import { htmlToPlainText } from './htmlToPlainText';
import { renderBodyWithLinks } from './renderBodyWithLinks';

/** Distribution channel slug this preview renders (PLAT-11038). */
export const CHANNEL_SLUG = 'facebook';

type FacebookViewport = 'desktop' | 'mobile';

type Props = {
  content: string;
  viewport?: FacebookViewport;
  authorName?: string;
  avatarUrl?: string;
};

const CHAR_LIMIT = 63206;
const DESKTOP_LINE_LIMIT = 7;
const MOBILE_LINE_LIMIT = 3;
const MOBILE_CHARS_PER_LINE = 50;

function isCutAtBlankLine(
  text: string,
  lineLimit: number,
  charsPerLine: number,
): boolean {
  const lines = text.split('\n');
  let visualLines = 0;
  for (let i = 0; i < lines.length; i++) {
    const len = lines[i].length;
    const lineSpan = len === 0 ? 1 : Math.ceil(len / charsPerLine);
    visualLines += lineSpan;
    if (visualLines >= lineLimit) {
      const isBlank = len === 0;
      const exactlyAtLimit = visualLines === lineLimit;
      const hasContentAfter = i < lines.length - 1;
      return isBlank && exactlyAtLimit && hasContentAfter;
    }
  }
  return false;
}

const FacebookPreview: React.FC<Props> = ({
  content,
  viewport = 'desktop',
  authorName,
  avatarUrl,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isClipped, setIsClipped] = React.useState(false);
  const textRef = React.useRef<HTMLDivElement>(null);

  const plainText = htmlToPlainText(content);

  const isOverLimit = plainText.length > CHAR_LIMIT;
  const overflowAmount = Math.max(0, plainText.length - CHAR_LIMIT);

  const isMobile = viewport === 'mobile';
  const lineLimit = isMobile ? MOBILE_LINE_LIMIT : DESKTOP_LINE_LIMIT;
  const cutOnBlankLine =
    isMobile && isCutAtBlankLine(plainText, lineLimit, MOBILE_CHARS_PER_LINE);

  React.useLayoutEffect(() => {
    if (isExpanded || !textRef.current) {
      setIsClipped(false);
      return;
    }
    const el = textRef.current;
    setIsClipped(el.scrollHeight > el.clientHeight + 1);
  }, [plainText, lineLimit, isExpanded]);

  const displayName = authorName || 'Your Page';

  const todayLabel = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Container>
      <PreviewCard viewport={viewport}>
        <PostHeader isMobile={isMobile}>
          <Avatar>
            {avatarUrl ? (
              <AvatarImage src={avatarUrl} alt={displayName} />
            ) : (
              <AvatarPlaceholder />
            )}
          </Avatar>
          <HeaderColumn>
            <NameRow>
              <DisplayName>{displayName}</DisplayName>
              <VerifiedWrapper aria-label="Verified page">
                <FacebookVerifiedBadge />
              </VerifiedWrapper>
            </NameRow>
            <MetaRow>
              <PostDate>{todayLabel}</PostDate>
              <MetaDot>·</MetaDot>
              <AudienceWrapper aria-label="Public">
                <EarthIcon width="12" height="12" fillColor="#65676b" />
              </AudienceWrapper>
            </MetaRow>
          </HeaderColumn>
          <MoreButton aria-label="More options">⋯</MoreButton>
        </PostHeader>

        <PostBody isMobile={isMobile}>
          <BodyTextWrapper>
            <ClampedText ref={textRef} clamped={!isExpanded} lines={lineLimit}>
              {renderBodyWithLinks(plainText, BlueLink)}
              {isExpanded && (
                <>
                  {' '}
                  <SeeLessInline
                    isMobile={isMobile}
                    onClick={() => setIsExpanded(false)}
                  >
                    … See less
                  </SeeLessInline>
                </>
              )}
            </ClampedText>
            {isClipped &&
              !isExpanded &&
              (cutOnBlankLine ? (
                <SeeMoreInlineLeft onClick={() => setIsExpanded(true)}>
                  … See more
                </SeeMoreInlineLeft>
              ) : (
                <SeeMoreFade
                  isMobile={isMobile}
                  onClick={() => setIsExpanded(true)}
                >
                  … See more
                </SeeMoreFade>
              ))}
          </BodyTextWrapper>
        </PostBody>

        <ReactionSummary isMobile={isMobile}>
          <LeftStats>
            <StatChip>
              <StatIcon>
                <FacebookActionLikeIcon
                  width="16"
                  height="16"
                  fillColor="#65676b"
                />
              </StatIcon>
              <StatCount>150</StatCount>
            </StatChip>
            <StatChip>
              <StatIcon>
                <FacebookCommentActionIcon
                  width="16"
                  height="16"
                  fillColor="#65676b"
                />
              </StatIcon>
              <StatCount>227</StatCount>
            </StatChip>
            <StatChip>
              <StatIcon>
                <FacebookShareActionIcon
                  width="16"
                  height="16"
                  fillColor="#65676b"
                />
              </StatIcon>
              <StatCount>63</StatCount>
            </StatChip>
          </LeftStats>
          <ReactionEmojiStack>
            <ReactionEmoji zIndex={3}>
              <FacebookLikeReactionBadge width="16" height="16" />
            </ReactionEmoji>
            <ReactionEmoji offset zIndex={2}>
              <FacebookLoveReactionBadge width="16" height="16" />
            </ReactionEmoji>
            <ReactionEmoji offset zIndex={1}>
              <FacebookWowReactionBadge width="16" height="16" />
            </ReactionEmoji>
          </ReactionEmojiStack>
        </ReactionSummary>
      </PreviewCard>

      <CharacterCount>
        {plainText.length.toLocaleString()} / {CHAR_LIMIT.toLocaleString()}{' '}
        characters
        {isOverLimit && <> · {overflowAmount.toLocaleString()} over</>}
      </CharacterCount>
    </Container>
  );
};

export default FacebookPreview;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PreviewCard = styled.div<{ viewport: 'desktop' | 'mobile' }>`
  background: white;
  border-radius: ${props => (props.viewport === 'mobile' ? '0' : '10px')};
  overflow: hidden;
  width: 100%;
  max-width: ${props => (props.viewport === 'mobile' ? '375px' : '500px')};
  margin: 0 auto;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial,
    sans-serif;
`;

const PostHeader = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${props => (props.isMobile ? '10px 12px 0' : '12px 16px 0')};
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
`;

const AvatarPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1877f2 0%, #0d4d96 100%);
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeaderColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DisplayName = styled.span`
  color: #050505;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3333;
`;

const BlueLink = styled.span`
  color: #1877f2;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const VerifiedWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #65676b;
  font-size: 13px;
  line-height: 1.3333;
`;

const PostDate = styled.span``;

const MetaDot = styled.span`
  font-size: 13px;
`;

const AudienceWrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;

const MoreButton = styled.button`
  background: transparent;
  border: none;
  color: #65676b;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  align-self: flex-start;
`;

const PostBody = styled.div<{ isMobile: boolean }>`
  padding: ${props => (props.isMobile ? '8px 12px 12px' : '8px 16px 12px')};
  color: #050505;
  font-size: 15px;
  line-height: 1.3333;
  word-wrap: break-word;
`;

const BodyTextWrapper = styled.div`
  position: relative;
`;

const ClampedText = styled.div<{ clamped: boolean; lines: number }>`
  white-space: pre-wrap;
  line-height: 1.3333;
  ${props =>
    props.clamped &&
    `
    max-height: calc(1.3333em * ${props.lines});
    overflow: hidden;
  `}
`;

const SeeMoreInlineLeft = styled.span`
  position: absolute;
  left: 0;
  bottom: 0;
  cursor: pointer;
  color: #65676b;
  font-weight: 400;
`;

const SeeLessInline = styled.span<{ isMobile: boolean }>`
  cursor: pointer;
  color: ${props => (props.isMobile ? '#65676b' : '#050505')};
  font-weight: ${props => (props.isMobile ? 400 : 600)};

  &:hover {
    text-decoration: ${props => (props.isMobile ? 'none' : 'underline')};
  }
`;

const SeeMoreFade = styled.span<{ isMobile: boolean }>`
  position: absolute;
  right: 0;
  bottom: 0;
  padding-left: 40px;
  background: linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff 30%);
  cursor: pointer;
  color: ${props => (props.isMobile ? '#65676b' : '#050505')};
  font-weight: ${props => (props.isMobile ? 400 : 600)};

  &:hover {
    text-decoration: ${props => (props.isMobile ? 'none' : 'underline')};
  }
`;

const ReactionSummary = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => (props.isMobile ? '10px 12px 8px' : '10px 16px 8px')};
  color: #65676b;
  font-size: 13px;
`;

const LeftStats = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 14px;
`;

const ReactionEmojiStack = styled.div`
  display: inline-flex;
  align-items: center;
`;

const ReactionEmoji = styled.span<{ offset?: boolean; zIndex?: number }>`
  display: inline-flex;
  align-items: center;
  position: relative;
  margin-left: ${props => (props.offset ? '-1px' : '0')};
  z-index: ${props => props.zIndex ?? 0};
  border-radius: 50%;
  box-shadow: 0 0 0 1px #ffffff;

  & > svg {
    display: block;
  }
`;

const StatChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const StatCount = styled.span`
  line-height: 1;
`;

const StatIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: #e4e6eb;

  & > svg {
    display: block;
    flex-shrink: 0;
  }
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 11px;
  color: var(--text-muted);
  padding: 0 4px;
`;
